import ExcelJS from "exceljs";

export function convertToCSV(objArray) {
  if (!Array.isArray(objArray) || objArray.length === 0) return "";

  const headers = Object.keys(objArray[0]);
  const escape = (val) => {
    let str = val == null ? "" : String(val);
    if (str.includes('"')) str = str.replace(/"/g, '""');
    return `"${str}"`;
  };

  // Header row
  let csv = headers.map(escape).join(",") + "\r\n";

  // Data rows
  for (const row of objArray) {
    const line = headers.map((key) => escape(row[key])).join(",");
    csv += line + "\r\n";
  }

  return csv;
}

// export function exportTransactionTypeDistributionCSV({
//   chartData,
//   storeName,
//   range,
//   fileName = "TransactionTypeDistribution.csv",
// }) {
//   if (!chartData || !chartData.labels || !chartData.datasets?.[0]?.data) {
//     console.error("exportTransactionTypeDistributionCSV: invalid chartData");
//     return;
//   }

//   // Determine date range label
//   const from = range.tran_date.$lte?.split("T")[0] || "";
//   const to = range.tran_date.$gte?.split("T")[0] || "";
//   const dateRange = `${from} ${to}`;

//   // Build rows
//   const rows = chartData.labels.map((label, idx) => ({
//     Brand: "Angels Pizza",
//     Store: storeName || "All Stores",
//     DateRange: dateRange,
//     TransactionType: label,
//     Count: chartData.datasets[0].data[idx] || 0,
//   }));

//   // Convert to CSV
//   const csvString = convertToCSV(rows);
//   if (!csvString) {
//     console.warn("exportTransactionTypeDistributionCSV: no data to export");
//     return;
//   }

//   // Trigger download
//   const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
//   const link = document.createElement("a");
//   link.href = URL.createObjectURL(blob);
//   link.download = fileName;
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
// }

export async function exportTransactionTypeDistributionCSV({
  chartData,
  storeName,
  range,
  fileName = "TransactionTypeDistribution.xlsx",
}) {
  if (!chartData?.labels?.length || !chartData?.datasets?.[0]?.data) {
    console.error("exportTransactionTypeDistributionXLSX: invalid chartData");
    return;
  }

  // FIX: usually gte = from, lte = to (your CSV code swapped)
  const fromISO = range?.tran_date?.$gte || "";
  const toISO = range?.tran_date?.$lte || "";

  const from = fromISO ? new Date(fromISO) : null;
  const to = toISO ? new Date(toISO) : null;

  const dateRangeLabel =
    from && to
      ? `${from.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })} - ${to.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })}`
      : "No date range selected";

  // Build rows (+ Total + Share %)
  const labels = chartData.labels;
  const data = chartData.datasets[0].data;

  const rows = labels.map((label, idx) => ({
    Brand: "Angels Pizza",
    Store: storeName || "All Stores",
    DateRange: dateRangeLabel,
    TransactionType: label,
    Count: Number(data[idx] ?? 0),
  }));

  const grandTotal = rows.reduce((sum, r) => sum + (r.Count || 0), 0);
  rows.forEach((r) => {
    r.Share = grandTotal > 0 ? r.Count / grandTotal : 0; // decimal (0..1)
  });

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Analytics Export";
  workbook.created = new Date();

  const sheet = workbook.addWorksheet("Type Distribution", {
    views: [{ state: "frozen", ySplit: 4 }],
  });

  // ===== Title =====
  sheet.mergeCells("A1:F1");
  const titleCell = sheet.getCell("A1");
  titleCell.value = "Transaction Type Distribution";
  titleCell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
  titleCell.alignment = { vertical: "middle", horizontal: "left" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF1F2937" },
  };
  sheet.getRow(1).height = 26;

  // ===== Meta =====
  sheet.mergeCells("A2:F2");
  const metaCell = sheet.getCell("A2");
  metaCell.value = `Brand: Angels Pizza   |   Store: ${
    storeName || "All Stores"
  }   |   Date Range: ${dateRangeLabel}`;
  metaCell.font = { italic: true, size: 11, color: { argb: "FF111827" } };
  metaCell.alignment = { vertical: "middle", horizontal: "left" };
  metaCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" },
  };
  sheet.getRow(2).height = 18;

  sheet.addRow([]);
  sheet.getRow(3).height = 6;

  // ===== Header + Data =====
  const headers = ["Brand", "Store", "Date Range", "Transaction Type", "Count"];
  sheet.addRow(headers);

  rows.forEach((r) => {
    sheet.addRow([r.Brand, r.Store, r.DateRange, r.TransactionType, r.Count]);
  });

  // Column widths
  sheet.columns = [
    { width: 16 }, // Brand
    { width: 18 }, // Store
    { width: 34 }, // Date Range
    { width: 22 }, // Transaction Type
    { width: 12 }, // Count
  ];

  const headerRowIndex = 4;
  const firstDataRow = headerRowIndex + 1;
  const lastDataRow = headerRowIndex + rows.length;

  // Style header row
  const headerRow = sheet.getRow(headerRowIndex);
  headerRow.height = 18;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF2563EB" },
    };
    cell.border = {
      top: { style: "thin", color: { argb: "FFE5E7EB" } },
      left: { style: "thin", color: { argb: "FFE5E7EB" } },
      bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
      right: { style: "thin", color: { argb: "FFE5E7EB" } },
    };
  });

  // Style body rows + formats
  for (let r = firstDataRow; r <= lastDataRow; r++) {
    const row = sheet.getRow(r);
    const isAlt = (r - firstDataRow) % 2 === 1;

    row.eachCell((cell, colNumber) => {
      cell.border = {
        top: { style: "thin", color: { argb: "FFE5E7EB" } },
        left: { style: "thin", color: { argb: "FFE5E7EB" } },
        bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
        right: { style: "thin", color: { argb: "FFE5E7EB" } },
      };

      // Alignment
      if (colNumber <= 4) {
        cell.alignment = { vertical: "middle", horizontal: "left" };
      } else {
        cell.alignment = { vertical: "middle", horizontal: "center" };
      }

      // Number formatting
      if (colNumber === 5) cell.numFmt = "#,##0";
      if (colNumber === 6) cell.numFmt = "0.00%";

      // Alternate row shading
      if (isAlt) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF9FAFB" },
        };
      }
    });
  }

  // Add TOTAL row
  const totalRowIndex = lastDataRow + 1;
  sheet.mergeCells(`A${totalRowIndex}:D${totalRowIndex}`);

  const totalLabelCell = sheet.getCell(`A${totalRowIndex}`);
  totalLabelCell.value = "TOTAL";
  totalLabelCell.font = { bold: true };
  totalLabelCell.alignment = { vertical: "middle", horizontal: "left" };
  totalLabelCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFEFF6FF" },
  };

  // Count sum (E) and Share sum (F should be 100%)
  const sumCountCell = sheet.getCell(`E${totalRowIndex}`);
  sumCountCell.value = { formula: `SUM(E${firstDataRow}:E${lastDataRow})` };
  sumCountCell.font = { bold: true };
  sumCountCell.alignment = { vertical: "middle", horizontal: "center" };
  sumCountCell.numFmt = "#,##0";
  sumCountCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFEFF6FF" },
  };

  // const sumShareCell = sheet.getCell(`F${totalRowIndex}`);
  // sumShareCell.value = { formula: `SUM(F${firstDataRow}:F${lastDataRow})` };
  // sumShareCell.font = { bold: true };
  // sumShareCell.alignment = { vertical: "middle", horizontal: "center" };
  // sumShareCell.numFmt = "0.00%";
  // sumShareCell.fill = {
  //   type: "pattern",
  //   pattern: "solid",
  //   fgColor: { argb: "FFEFF6FF" },
  // };

  // Borders for TOTAL row
  for (let c = 1; c <= 6; c++) {
    const cell = sheet.getRow(totalRowIndex).getCell(c);
    cell.border = {
      top: { style: "thin", color: { argb: "FF93C5FD" } },
      left: { style: "thin", color: { argb: "FF93C5FD" } },
      bottom: { style: "thin", color: { argb: "FF93C5FD" } },
      right: { style: "thin", color: { argb: "FF93C5FD" } },
    };
  }

  // Auto-filter
  sheet.autoFilter = {
    from: { row: headerRowIndex, column: 1 },
    to: { row: headerRowIndex, column: headers.length },
  };

  // ===== Download (no file-saver) =====
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(link.href), 500);
}
